import { useCallback, useEffect, useState } from 'react';
import type { ReportGroupBy, RevenueReportView } from '@cuito/types';
import { formatBeijingDate } from '@cuito/ui';
import { reportsApi } from '../api/reports';
import { readServerMessage } from '../lib/apiError';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../lib/schemeForm';

/**
 * 报表（`05-ui-design-system.md` §3.2 的 `/reports`，R39 社团版）。
 *
 * ## 只有自己一行（契约 §16.1 的可见范围表）
 *
 * 社团令牌的 `tenants[]` 长度**恒为 1** —— 服务端强制注入 `tenantId`，**没有 `scope` 参数**。
 * 本页因此不渲染任何"选择社团/选择地点"的控件。
 *
 * ## 不做地点 / 展会下钻（**Phase 9**）
 *
 * 规格 §3.2 的 `/reports` 行原有「地点选择 / 请选择展会或门店」，已由控制器于 `eb7ec5f`
 * 就地删除：`scope` 下钻属 Phase 9。本页只有 `groupBy`（日 / 月 / 年）分段按钮。
 *
 * ## 金额只显示、不计算
 *
 * 四个数字全部来自服务端字符串：毛额 `totalRevenue`、已退 `totalRefund`、
 * 净额 `tenants[0].netAmount`、订单数 `totalOrders`。
 * **净额不做减法**：`grossAmount − refundAmount` 的唯一实现在 `@cuito/types` 的 `revenueReportOf`。
 * 报表端点的金额是**十进制元字符串**（§1.7），所以走 `formatServerYuan`
 * （分位整数走 `formatCents`，见 `ParticipationsPage` 的同名辅助函数）。
 */
const GROUP_BY_LABELS: Record<ReportGroupBy, string> = { DAY: '日', MONTH: '月', YEAR: '年' };

/**
 * 服务端金额 → 展示串；`null` / 读不出来一律落到 `金额以服务端为准`。
 *
 * 只是 `formatServerYuan` 的一层 `null` 兜底（那个函数的入参不含 `null`），
 * **金额的解析与格式化仍然只有一份实现**（`@cuito/ui` 的 `centsFromYuan` + `formatYuan`）。
 */
function amountText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return AMOUNT_UNAVAILABLE_TEXT;
  return formatServerYuan(value) ?? AMOUNT_UNAVAILABLE_TEXT;
}

export default function ReportsPage() {
  const [groupBy, setGroupBy] = useState<ReportGroupBy>('DAY');
  const [report, setReport] = useState<RevenueReportView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setReport(await reportsApi.revenue({ groupBy }));
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [groupBy]);

  useEffect(() => {
    void load();
  }, [load]);

  const own = report?.tenants[0] ?? null;

  return (
    <div className="max-w-7xl" data-testid="tenant-reports-page">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">报表</h1>

      <div className="mb-4 flex gap-2">
        {(['DAY', 'MONTH', 'YEAR'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={groupBy === value}
            onClick={() => setGroupBy(value)}
            className={`rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
              groupBy === value ? 'bg-black text-white' : 'bg-white text-black'
            }`}
          >
            {GROUP_BY_LABELS[value]}
          </button>
        ))}
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error !== null && <p className="font-bold text-red-600">{error}</p>}

      {!loading && error === null && report !== null && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border-2 border-black bg-white p-6">
              <p className="text-sm font-bold text-gray-600">毛额</p>
              <p className="mt-2 text-2xl font-bold text-green-600">
                {amountText(report.totalRevenue)}
              </p>
            </div>
            <div className="rounded-lg border-2 border-black bg-white p-6">
              <p className="text-sm font-bold text-gray-600">已退</p>
              <p className="mt-2 text-2xl font-bold">{amountText(report.totalRefund)}</p>
            </div>
            <div className="rounded-lg border-2 border-black bg-white p-6">
              <p className="text-sm font-bold text-gray-600">净额</p>
              <p className="mt-2 text-2xl font-bold" data-testid="tenant-report-net">
                {amountText(own?.netAmount)}
              </p>
            </div>
            <div className="rounded-lg border-2 border-black bg-white p-6">
              <p className="text-sm font-bold text-gray-600">订单数</p>
              <p className="mt-2 text-2xl font-bold">{report.totalOrders}</p>
            </div>
          </div>

          {report.tenants.length === 0 ? (
            <p className="text-gray-500">暂无业务数据</p>
          ) : (
            <table className="w-full rounded-lg border-2 border-black">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">社团</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">毛额</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">已退</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">净额</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">订单数</th>
                </tr>
              </thead>
              <tbody>
                {report.tenants.map((row) => (
                  <tr key={row.tenantId} data-testid="tenant-report-row" data-tenant-id={row.tenantId}>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">
                      {row.tenantName}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm text-green-600">
                      {amountText(row.grossAmount)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm">
                      {amountText(row.refundAmount)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">
                      {amountText(row.netAmount)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.orderCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {report.series.length > 0 && (
            <table className="mt-6 w-full rounded-lg border-2 border-black">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">期间</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">营收</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">已退</th>
                  <th className="border-b-2 border-black px-4 py-3 text-left font-bold">订单数</th>
                </tr>
              </thead>
              <tbody>
                {report.series.map((point) => (
                  <tr key={point.period}>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm">
                      {formatBeijingDate(point.period)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm text-green-600">
                      {amountText(point.revenue)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm">
                      {amountText(point.refund)}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3 text-sm">{point.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
